import {
  Box,
  Button,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  MenuItem,
  Paper,
  Popper,
  TextField,
  useTheme,
  CircularProgress,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import React, { useRef, useState, useEffect, useCallback } from "react";

import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { tokens } from "../../../../../theme";
import { RootState } from "../../../../../store/Index";
import { Venue } from "../../../../../types/venue";
import apiService from "../../../../../service/Api";
import { BookingData } from "../../../../../types/bookings";
import { initialValues } from "../../../../bookings/AddBooking";
import { buildBookingFormData } from "../../../../bookings/utils/add-booking-form-data";
import BookingInputCard from "../../../../mms-landingpage/components/BookingInputCard";
import DatePickerDropdown from "../../../../proposal/service-quote-form/components/DatePicker";
import TimePickerDropdown from "../../../../proposal/service-quote-form/components/TimePicker";
import QuickBookingHeader from "../../../../mms-landingpage/components/QuickBookingHeader";
import CustomSelect from "../../../../../components/ui/Select";
import CustomTextField from "../../../../../components/ui/Textfield";
import LocationPicker from "../../../../proposal/service-quote-form/components/LocationPicker";
import { MuiTelInput, MuiTelInputInfo } from "mui-tel-input";

// ── Constants ─────────────────────────────────────────────────────────────────

const BOOKING_TYPES = [
  { label: "Funeral", value: "FUNERAL" },
  { label: "Conference", value: "CONFERENCE" },
];

// ── Nominatim types ───────────────────────────────────────────────────────────

interface NominatimAddress {
  road?: string;
  house_number?: string;
  suburb?: string;
  neighbourhood?: string;
  city?: string;
  town?: string;
  village?: string;
  county?: string;
  state?: string;
  postcode?: string;
}

interface NominatimResult {
  place_id: string;
  display_name: string;
  name?: string;
  address: NominatimAddress;
}

interface VenueSuggestion {
  id: string;
  source: "backend" | "nominatim";
  displayName: string;
  subtext: string;
  venue_name: string;
  address_one: string;
  address_two: string;
  suburb: string;
  city: string;
}

type FillSource = "backend" | "nominatim" | "manual" | null;

function parseNominatim(
  result: NominatimResult,
): Omit<VenueSuggestion, "id" | "source" | "displayName" | "subtext"> {
  const a = result.address;
  const addressParts = [a.house_number, a.road].filter(Boolean);
  const address_one = addressParts.join(" ") || "";
  const address_two = a.neighbourhood ?? a.postcode ?? "";
  const suburb = a.suburb ?? a.county ?? "";
  const city = a.city ?? a.town ?? a.village ?? a.state ?? "";
  const venue_name =
    result.name && result.name !== result.display_name
      ? result.name
      : result.display_name.split(",")[0].trim();
  return { venue_name, address_one, address_two, suburb, city };
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface PackageOption {
  id: number;
  name: string;
  price: number;
}

interface QuickInfoState {
  first_name: string;
  surname: string;
  email: string;
  phone: string;
  notes: string;
  relationship_to_deceased: string;
  end_time: string;
  end_date: string;
  deceased_name: string;
  deceased_surname: string;
  deceased_salutation: string;
  date_of_birth: string;
  date_of_death: string;
  gender: string;
  type_of_service: string;
  conference_name: string;
  conference_type: string;
  selected_package_id: number | null;
}

const initialQuickInfo: QuickInfoState = {
  first_name: "",
  surname: "",
  email: "",
  phone: "",
  notes: "",
  relationship_to_deceased: "",
  end_time: "",
  end_date: "",
  deceased_name: "",
  deceased_surname: "",
  deceased_salutation: "",
  date_of_birth: "",
  date_of_death: "",
  gender: "",
  type_of_service: "",
  conference_name: "",
  conference_type: "",
  selected_package_id: null,
};

interface CreateBookingFormProps {
  onSuccess?: () => void;
  buttonColor?: string;
}

// ── SectionLabel (stable, outside parent) ────────────────────────────────────

const SectionLabel = ({ children }: { children: string }) => (
  <Typography
    variant="caption"
    fontWeight={600}
    color="text.secondary"
    sx={{
      textTransform: "uppercase",
      letterSpacing: 0.8,
      display: "block",
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

// ── Smart Venue Search (stable, outside parent) ───────────────────────────────

interface SmartVenueSearchProps {
  searchTerm: string;
  suggestions: VenueSuggestion[];
  loading: boolean;
  showDropdown: boolean;
  fillSource: FillSource;
  needsManual: boolean;
  venueData: Omit<
    VenueSuggestion,
    "id" | "source" | "displayName" | "subtext"
  > | null;
  statusChip: React.ReactNode;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelect: (item: VenueSuggestion) => void;
  onFocus: () => void;
  onBlur: () => void;
  onManualFieldChange: (field: string, value: string) => void;
}

const SmartVenueSearch = React.memo(function SmartVenueSearch({
  searchTerm,
  suggestions,
  loading,
  showDropdown,
  needsManual,
  venueData,
  statusChip,
  onSearchChange,
  onSelect,
  onFocus,
  onBlur,
  onManualFieldChange,
}: SmartVenueSearchProps) {
  return (
    <Box sx={{ position: "relative" }}>
      <TextField
        fullWidth
        size="small"
        label="Venue"
        value={searchTerm}
        onChange={onSearchChange}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder="Search for a venue…"
        autoComplete="off"
        inputProps={{
          autoComplete: "off",
          autoCorrect: "off",
          autoCapitalize: "off",
          spellCheck: false,
        }}
        InputProps={{
          startAdornment: (
            <SearchIcon sx={{ color: "text.disabled", mr: 1, fontSize: 18 }} />
          ),
          endAdornment: loading ? (
            <CircularProgress size={16} sx={{ ml: 1 }} />
          ) : null,
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 1,
            "& fieldset": {
              borderColor: showDropdown
                ? "primary.main"
                : needsManual
                  ? "warning.main"
                  : undefined,
            },
          },
        }}
      />

      {statusChip}

      {/* Dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <Paper
          elevation={4}
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1400,
            maxHeight: 260,
            overflowY: "auto",
            mt: 0.5,
            borderRadius: 1,
          }}
        >
          <List disablePadding>
            {suggestions.map((item, index) => (
              <Box key={item.id}>
                <ListItemButton
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelect(item);
                  }}
                  sx={{ py: 1.25, px: 2 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {item.source === "backend" ? (
                      <StorefrontOutlinedIcon
                        fontSize="small"
                        color="success"
                      />
                    ) : (
                      <LocationOnOutlinedIcon
                        fontSize="small"
                        color="primary"
                      />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <span>{item.displayName}</span>
                      </Box>
                    }
                    secondary={item.subtext}
                    primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                    secondaryTypographyProps={{
                      fontSize: 11,
                      noWrap: true,
                      sx: { overflow: "hidden", textOverflow: "ellipsis" },
                    }}
                  />
                </ListItemButton>
                {index < suggestions.length - 1 && <Divider component="li" />}
              </Box>
            ))}
          </List>
        </Paper>
      )}

      {/* Manual address entry */}
      {needsManual && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mt: 1.5,
            p: 1.5,
            borderRadius: 1,
            border: "1px dashed",
            borderColor: "warning.light",
            bgcolor: "warning.50",
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: "warning.dark", fontWeight: 500 }}
          >
            We could not find this venue automatically. Please enter the address
            so we can make sure the booking goes to the right place.
          </Typography>
          {[
            { field: "address_one", label: "Address *" },
            { field: "address_two", label: "Address 2" },
            { field: "suburb", label: "Suburb" },
            { field: "city", label: "City *" },
          ].map(({ field, label }) => (
            <TextField
              key={field}
              label={label}
              size="small"
              fullWidth
              autoComplete="off"
              value={
                venueData
                  ? ((venueData as Record<string, string>)[field] ?? "")
                  : ""
              }
              onChange={(e) => onManualFieldChange(field, e.target.value)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
});

// ── ExpandedDetails (stable, outside parent) ──────────────────────────────────

interface ExpandedDetailsProps {
  smartSearchTerm: string;
  smartSuggestions: VenueSuggestion[];
  smartLoading: boolean;
  smartShowDropdown: boolean;
  smartFillSource: FillSource;
  smartNeedsManual: boolean;
  smartVenueData: Omit<
    VenueSuggestion,
    "id" | "source" | "displayName" | "subtext"
  > | null;
  statusChip: React.ReactNode;
  packagesLoading: boolean;
  packages: PackageOption[];
  selectedPackageId: number | null;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelect: (item: VenueSuggestion) => void;
  onShowDropdown: (show: boolean) => void;
  onPackageChange: (id: number) => void;
  onManualFieldChange: (field: string, value: string) => void;
  bookingType: string;
  serviceDate: string;
  startTime: string;
  quickInfo: QuickInfoState;
  fieldSx: object;
  onQuickInfoChange: (field: string, value: string) => void;
  onPhoneChange: (value: string) => void;
}

const ExpandedDetails = React.memo(function ExpandedDetails({
  smartSearchTerm,
  smartSuggestions,
  smartLoading,
  smartShowDropdown,
  smartFillSource,
  smartNeedsManual,
  smartVenueData,
  statusChip,
  packagesLoading,
  packages,
  selectedPackageId,
  bookingType,
  serviceDate,
  startTime,
  quickInfo,
  fieldSx,
  onSearchChange,
  onSelect,
  onShowDropdown,
  onPackageChange,
  onManualFieldChange,
  onQuickInfoChange,
  onPhoneChange,
}: ExpandedDetailsProps) {
  const isFuneral = bookingType === "FUNERAL";
  const isConference = bookingType === "CONFERENCE";

  return (
    <>
      {/* ── Client Details (extended) ── */}
      {/* ── Client Details (extended) ── */}
      <Divider />
      <Box>
        <SectionLabel>Client Details</SectionLabel>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <MuiTelInput
            size="small"
            label="Phone"
            value={quickInfo.phone}
            onChange={(value: string, _info: MuiTelInputInfo) => {
              void _info;
              onPhoneChange(value);
            }}
            defaultCountry="ZW"
            variant="outlined"
            fullWidth
            sx={fieldSx}
          />
          <TextField
            label="Email"
            size="small"
            fullWidth
            sx={fieldSx}
            value={quickInfo.email}
            onChange={(e) => onQuickInfoChange("email", e.target.value)}
          />
          {isFuneral && (
            <TextField
              label="Relationship to Deceased"
              size="small"
              fullWidth
              sx={fieldSx}
              value={quickInfo.relationship_to_deceased}
              onChange={(e) =>
                onQuickInfoChange("relationship_to_deceased", e.target.value)
              }
            />
          )}
          <TextField
            label="Notes"
            size="small"
            fullWidth
            multiline
            rows={3}
            sx={fieldSx}
            value={quickInfo.notes}
            onChange={(e) => onQuickInfoChange("notes", e.target.value)}
          />
        </Box>
      </Box>

      {/* ── Deceased Details (extended, funeral only) ── */}
      {isFuneral && (
        <>
          <Divider />
          <Box>
            <SectionLabel>Deceased Details</SectionLabel>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <FormControl fullWidth size="small">
                <CustomSelect
                  label="Title"
                  value={quickInfo.deceased_salutation}
                  onChange={(e) =>
                    onQuickInfoChange(
                      "deceased_salutation",
                      e.target.value as string,
                    )
                  }
                >
                  {[
                    "Mr",
                    "Mrs",
                    "Ms",
                    "Miss",
                    "Dr",
                    "Prof",
                    "Rev",
                    "Other",
                  ].map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </FormControl>
              <TextField
                label="Date of Birth"
                size="small"
                fullWidth
                type="date"
                sx={fieldSx}
                value={quickInfo.date_of_birth}
                onChange={(e) =>
                  onQuickInfoChange("date_of_birth", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Date of Death"
                size="small"
                fullWidth
                type="date"
                sx={fieldSx}
                value={quickInfo.date_of_death}
                onChange={(e) =>
                  onQuickInfoChange("date_of_death", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        </>
      )}

      {/* ── Service Schedule ── */}
      <Divider />
      <Box>
        <SectionLabel>Service Schedule</SectionLabel>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <TextField
            label="Service Date"
            size="small"
            fullWidth
            type="date"
            sx={fieldSx}
            value={serviceDate}
            disabled
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Start Time"
            size="small"
            fullWidth
            type="time"
            sx={fieldSx}
            value={startTime}
            disabled
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Time"
            size="small"
            fullWidth
            type="time"
            sx={fieldSx}
            value={quickInfo.end_time}
            onChange={(e) => onQuickInfoChange("end_time", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          {isConference && (
            <TextField
              label="End Date"
              size="small"
              fullWidth
              type="date"
              sx={fieldSx}
              value={quickInfo.end_date}
              onChange={(e) => onQuickInfoChange("end_date", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          )}
        </Box>
      </Box>

      {/* ── Venue Details ── */}
      <Divider />
      <Box>
        <SectionLabel>Venue Details</SectionLabel>
        <SmartVenueSearch
          searchTerm={smartSearchTerm}
          suggestions={smartSuggestions}
          loading={smartLoading}
          showDropdown={smartShowDropdown}
          fillSource={smartFillSource}
          needsManual={smartNeedsManual}
          venueData={smartVenueData}
          statusChip={statusChip}
          onSearchChange={onSearchChange}
          onSelect={onSelect}
          onFocus={() => smartSuggestions.length > 0 && onShowDropdown(true)}
          onBlur={() => setTimeout(() => onShowDropdown(false), 200)}
          onManualFieldChange={onManualFieldChange}
        />
      </Box>

      {/* ── Package ── */}
      <Divider />
      <Box>
        <SectionLabel>Package</SectionLabel>
        {packagesLoading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">
              Loading…
            </Typography>
          </Box>
        ) : packages.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No packages available.
          </Typography>
        ) : (
          <RadioGroup
            value={selectedPackageId ?? ""}
            onChange={(e) => onPackageChange(Number(e.target.value))}
          >
            {packages.map((pkg) => (
              <FormControlLabel
                key={pkg.id}
                value={pkg.id}
                control={<Radio size="small" />}
                label={
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Typography variant="body2">{pkg.name}</Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 2 }}
                    >
                      ${pkg.price.toFixed(2)}
                    </Typography>
                  </Box>
                }
                sx={{ mr: 0, "& .MuiFormControlLabel-label": { flex: 1 } }}
              />
            ))}
          </RadioGroup>
        )}
      </Box>
    </>
  );
});

// ── LocationQuickPick (stable, outside parent) ────────────────────────────────

interface LocationQuickPickProps {
  title: string;
  funeralLocation: string;
  locationAnchor: HTMLElement | null;

  locationBoxRef: React.RefObject<HTMLDivElement | null>;
  borderColor: string;
  hoverBorderColor: string;
  onOpen: () => void;
  onClose: () => void;
  onChange: (loc: string) => void;
  onDelete: () => void;
}

const LocationQuickPick = React.memo(function LocationQuickPick({
  title,
  funeralLocation,
  locationAnchor,
  locationBoxRef,
  borderColor,
  hoverBorderColor,
  onOpen,
  onClose,
  onChange,
  onDelete,
}: LocationQuickPickProps) {
  return (
    <Box ref={locationBoxRef}>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mb: 0.5,
          color: "text.secondary",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: 0.8,
          fontSize: "0.65rem",
        }}
      >
        Location Type
      </Typography>
      <Paper
        variant="outlined"
        onClick={onOpen}
        sx={{
          display: "flex",
          alignItems: "center",
          px: 1.5,
          py: 0.9,
          borderRadius: "12px",
          cursor: "pointer",
          borderColor: funeralLocation ? "primary.main" : borderColor,
          "&:hover": { borderColor: hoverBorderColor },
        }}
      >
        <LocationOnOutlinedIcon
          sx={{
            color: funeralLocation ? "primary.main" : "text.disabled",
            mr: 1,
            fontSize: 18,
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: funeralLocation ? "text.primary" : "text.disabled",
            fontSize: "0.875rem",
            flex: 1,
          }}
        >
          {funeralLocation || "Select location type"}
        </Typography>
        {funeralLocation && (
          <Chip
            label={funeralLocation}
            size="small"
            color="primary"
            variant="outlined"
            onDelete={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            sx={{ height: 20, fontSize: 11 }}
          />
        )}
      </Paper>
      <LocationPicker
        open={Boolean(locationAnchor)}
        anchorEl={locationAnchor}
        onClose={onClose}
        value={funeralLocation}
        onChange={(loc) => {
          onChange(loc);
          onClose();
        }}
        title={title}
      />
    </Box>
  );
});

// ── Main component ────────────────────────────────────────────────────────────

const ReferralQuickBookingForm: React.FC<CreateBookingFormProps> = ({
  buttonColor,
  onSuccess,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const [when, setWhen] = useState("");
  const [bookingType, setBookingType] = useState("");
  const [whatTime, setWhatTime] = useState("");
  const [whenAnchor, setWhenAnchor] = useState<HTMLElement | null>(null);
  const [typeAnchor, setTypeAnchor] = useState<HTMLElement | null>(null);
  const [timeAnchor, setTimeAnchor] = useState<HTMLElement | null>(null);
  const typeBoxRef = useRef<HTMLDivElement | null>(null);
  const timeBoxRef = useRef<HTMLDivElement | null>(null);

  const [quickInfoOpen, setQuickInfoOpen] = useState(false);
  const [quickInfo, setQuickInfo] = useState<QuickInfoState>(initialQuickInfo);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showFullDetails, setShowFullDetails] = useState(false);

  const [packages, setPackages] = useState<PackageOption[]>([]);
  const [packagesLoading, setPackagesLoading] = useState(false);

  // ── Location quick-pick ───────────────────────────────────────────────────
  const [funeralLocation, setFuneralLocation] = useState("");
  const [locationAnchor, setLocationAnchor] = useState<HTMLElement | null>(
    null,
  );
  const locationBoxRef = useRef<HTMLDivElement>(null);

  // ── Smart venue search state ──────────────────────────────────────────────
  const [smartSearchTerm, setSmartSearchTerm] = useState("");
  const [smartSuggestions, setSmartSuggestions] = useState<VenueSuggestion[]>(
    [],
  );
  const [smartLoading, setSmartLoading] = useState(false);
  const [smartShowDropdown, setSmartShowDropdown] = useState(false);
  const [smartFillSource, setSmartFillSource] = useState<FillSource>(null);
  const [smartVenueData, setSmartVenueData] = useState<Omit<
    VenueSuggestion,
    "id" | "source" | "displayName" | "subtext"
  > | null>(null);

  const [openVenueDialog, setOpenVenueDialog] = useState(false);
  const [addingVenue, setAddingVenue] = useState(false);
  const [newVenue, setNewVenue] = useState<Venue>({
    name: "",
    address_one: "",
    address_two: "",
    suburb: "",
    city: "Harare",
  });

  // ── Packages ──────────────────────────────────────────────────────────────

  const fetchPackages = useCallback(async (type: string) => {
    setPackagesLoading(true);
    try {
      const map: Record<string, string> = {
        FUNERAL: "Funeral",
        CONFERENCE: "Conference",
      };
      const res = await apiService.get<{
        results: { id: number; name: string; base_price: string }[];
      }>(
        `/api/packages/package/?package_type=${map[type] ?? type}&page_size=20`,
      );
      setPackages(
        (res.data.results || []).map((p) => ({
          id: p.id,
          name: p.name,
          price: parseFloat(p.base_price) || 0,
        })),
      );
    } catch {
      /* silently ignore */
    } finally {
      setPackagesLoading(false);
    }
  }, []);

  // ── Smart venue dual search ───────────────────────────────────────────────

  useEffect(() => {
    if (smartSearchTerm.length < 3) {
      setSmartSuggestions([]);
      setSmartShowDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      setSmartLoading(true);
      try {
        const [backendResult, nominatimResult] = await Promise.allSettled([
          apiService
            .get<{
              results: {
                id: number;
                name: string;
                address_one?: string;
                address_two?: string;
                suburb?: string;
                city?: string;
              }[];
            }>(
              `/api/venues/venue/?search=${encodeURIComponent(smartSearchTerm)}`,
            )
            .then((res) =>
              (res.data.results ?? []).map(
                (v): VenueSuggestion => ({
                  id: `backend-${v.id}`,
                  source: "backend",
                  displayName: v.name,
                  subtext:
                    [v.address_one, v.city].filter(Boolean).join(", ") ||
                    "Already in the system",
                  venue_name: v.name,
                  address_one: v.address_one ?? "",
                  address_two: v.address_two ?? "",
                  suburb: v.suburb ?? "",
                  city: v.city ?? "",
                }),
              ),
            ),
          fetch(
            `https://nominatim.openstreetmap.org/search?${new URLSearchParams({
              q: smartSearchTerm,
              format: "json",
              addressdetails: "1",
              limit: "5",
              dedupe: "1",
              countrycodes: "zw",
            })}`,
            { headers: { "Accept-Language": "en" } },
          )
            .then((r) => r.json() as Promise<NominatimResult[]>)
            .then((data) =>
              data.map((item): VenueSuggestion => {
                const parsed = parseNominatim(item);
                return {
                  id: `nominatim-${item.place_id}`,
                  source: "nominatim",
                  displayName: parsed.venue_name,
                  subtext: item.display_name,
                  ...parsed,
                };
              }),
            ),
        ]);

        const backendItems =
          backendResult.status === "fulfilled" ? backendResult.value : [];
        const nominatimItems =
          nominatimResult.status === "fulfilled" ? nominatimResult.value : [];
        const combined = [...backendItems, ...nominatimItems];

        setSmartSuggestions(combined);
        setSmartShowDropdown(combined.length > 0);
        if (combined.length === 0) setSmartFillSource("manual");
      } catch {
        setSmartSuggestions([]);
        setSmartShowDropdown(false);
        setSmartFillSource("manual");
      } finally {
        setSmartLoading(false);
      }
    }, 450);

    return () => clearTimeout(timer);
  }, [smartSearchTerm]);

  const handleSmartSelect = useCallback((item: VenueSuggestion) => {
    setSmartSearchTerm(item.venue_name);
    setSmartShowDropdown(false);
    setSmartSuggestions([]);
    setSmartFillSource(item.source);
    setSmartVenueData({
      venue_name: item.venue_name,
      address_one: item.address_one,
      address_two: item.address_two,
      suburb: item.suburb,
      city: item.city,
    });
  }, []);

  const handleSmartSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setSmartSearchTerm(val);
      setSmartFillSource(null);
      setSmartSuggestions([]);
      setSmartShowDropdown(false);
      setSmartVenueData(
        val
          ? {
              venue_name: val,
              address_one: "",
              address_two: "",
              suburb: "",
              city: "",
            }
          : null,
      );
    },
    [],
  );

  const handleManualFieldChange = useCallback(
    (field: string, value: string) => {
      setSmartVenueData((prev) => (prev ? { ...prev, [field]: value } : prev));
    },
    [],
  );

  const handlePackageChange = useCallback((id: number) => {
    setQuickInfo((p) => ({ ...p, selected_package_id: id }));
  }, []);

  const smartNeedsManual =
    smartFillSource === "manual" && smartSearchTerm.trim().length > 0;

  // ── Add venue dialog ──────────────────────────────────────────────────────

  const handleAddVenue = async () => {
    setAddingVenue(true);
    try {
      const res = await apiService.post<Venue>("/api/venues/venue/", newVenue);
      setSmartVenueData({
        venue_name: res.data.name,
        address_one: res.data.address_one ?? "",
        address_two: res.data.address_two ?? "",
        suburb: res.data.suburb ?? "",
        city: res.data.city ?? "",
      });
      setSmartSearchTerm(res.data.name);
      setSmartFillSource("backend");
      toast.success("Venue added successfully");
      setOpenVenueDialog(false);
      setNewVenue({
        name: "",
        address_one: "",
        address_two: "",
        suburb: "",
        city: "Harare",
      });
    } catch {
      toast.error("Failed to add venue.");
    } finally {
      setAddingVenue(false);
    }
  };

  // ── Formatters ────────────────────────────────────────────────────────────

  const formatDate = (date: string) => {
    if (!date) return "Add date";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (time: string) => {
    if (!time) return "Add time";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatType = (type: string) => {
    if (!type) return "Select type";
    return BOOKING_TYPES.find((t) => t.value === type)?.label || type;
  };

  const handleQuickInfoChange = useCallback((field: string, value: string) => {
    setQuickInfo((p) => ({ ...p, [field]: value }));
  }, []);
  // ── Build prefilled booking data ──────────────────────────────────────────

  const buildPrefilledData = (): BookingData => {
    const selectedPkg = packages.find(
      (p) => p.id === quickInfo.selected_package_id,
    );
    const combinedNotes =
      [quickInfo.notes, funeralLocation ? `Location: ${funeralLocation}` : ""]
        .filter(Boolean)
        .join("\n") || undefined;

    return {
      ...initialValues,
      booking_type: bookingType,
      date_of_service: when,
      start_time: whatTime || undefined,
      end_time: quickInfo.end_time || undefined,
      start_date_of_event: when,
      end_date_of_event: quickInfo.end_date || null,
      venue_ids: [],
      venue_name: smartVenueData?.venue_name ?? undefined,
      address_one: smartVenueData?.address_one ?? undefined,
      address_two: smartVenueData?.address_two ?? undefined,
      suburb: smartVenueData?.suburb ?? undefined,
      city: smartVenueData?.city ?? undefined,
      service_address: smartVenueData?.address_one ?? undefined,
      service_city: smartVenueData?.city ?? undefined,
      first_name: quickInfo.first_name || undefined,
      surname: quickInfo.surname || undefined,
      phone: quickInfo.phone || undefined,
      email: quickInfo.email || undefined,
      notes: combinedNotes,
      deceased_name: quickInfo.deceased_name || undefined,
      deceased_surname: quickInfo.deceased_surname || undefined,
      deceased_salutation: quickInfo.deceased_salutation || undefined,
      date_of_birth: quickInfo.date_of_birth || undefined,
      date_of_death: quickInfo.date_of_death || undefined,
      gender: quickInfo.gender || undefined,
      type_of_service: quickInfo.type_of_service || null,
      title: quickInfo.conference_name || undefined,
      conference_type: quickInfo.conference_type || null,
      ...(selectedPkg
        ? {
            package: {
              id: selectedPkg.id,
              name: selectedPkg.name,
              price: selectedPkg.price,
            },
            package_total: selectedPkg.price,
            total: selectedPkg.price,
          }
        : {}),
    };
  };

  // ── Dialog open ───────────────────────────────────────────────────────────

  const handleCreateBooking = () => {
    if (!when || !bookingType || !whatTime) {
      toast.error("Please fill in all fields.");
      return;
    }
    setQuickInfo(initialQuickInfo);
    setSmartSearchTerm("");
    setSmartVenueData(null);
    setSmartFillSource(null);
    setShowFullDetails(false);
    setFuneralLocation("");
    setPackages([]);
    fetchPackages(bookingType);
    setQuickInfoOpen(true);
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const contactRes = await apiService.post<{ id: number }>(
        "/api/contacts/contact/",
        {
          first_name: quickInfo.first_name,
          last_name: quickInfo.surname,
          phone: quickInfo.phone,
          contact_type: "individual",
        },
      );

      // ── Resolve venue ─────────────────────────────────────────────────────
      let venueId: string | null = null;
      if (smartVenueData?.venue_name) {
        try {
          const searchRes = await apiService.get<{
            results: { id: number; name: string }[];
          }>(
            `/api/venues/venue/?search=${encodeURIComponent(smartVenueData.venue_name)}`,
          );

          const results = searchRes.data.results ?? [];
          const exactMatch = results.find(
            (v) =>
              v.name.toLowerCase() === smartVenueData.venue_name.toLowerCase(),
          );
          const match = exactMatch ?? results[0];

          if (match) {
            venueId = String(match.id);
          } else {
            const createRes = await apiService.post<{ id: number }>(
              "/api/venues/venue/",
              {
                name: smartVenueData.venue_name,
                address_one:
                  smartVenueData.address_one || smartVenueData.venue_name,
                address_two: smartVenueData.address_two || "",
                suburb: smartVenueData.suburb || "",
                city: smartVenueData.city || "",
              },
            );
            venueId = String(createRes.data.id);
          }
        } catch {
          console.warn("Venue resolution failed — proceeding without venue_id");
        }
      }

      const prebuilt = buildPrefilledData();
      const data: BookingData = {
        ...prebuilt,
        contact_id: contactRes.data.id,
        address_one:
          smartVenueData?.address_one ||
          smartVenueData?.venue_name ||
          prebuilt.address_one ||
          "N/A",
        service_address:
          smartVenueData?.address_one || smartVenueData?.venue_name || "N/A",
        service_city: smartVenueData?.city || prebuilt.service_city || "N/A",
        ...(venueId ? { venue_ids: [venueId] } : {}),
      };

      const payload = buildBookingFormData(data);
      await apiService.post(
        "/api/bookings/booking/create-fullbooking/",
        payload,
      );

      toast.success("Booking created successfully");
      setQuickInfoOpen(false);
      setWhen("");
      setBookingType("");
      setWhatTime("");
      onSuccess?.();
    } catch {
      toast.error("Failed to create booking");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Smart venue status chip ───────────────────────────────────────────────

  const smartStatusChip = () => {
    if (!smartSearchTerm || smartLoading) return null;
    if (smartFillSource === "backend")
      return (
        <Chip
          size="small"
          icon={<StorefrontOutlinedIcon sx={{ fontSize: "14px !important" }} />}
          label="Address details filled in automatically"
          color="success"
          variant="outlined"
          sx={{ mt: 0.75, height: 24, fontSize: 11 }}
        />
      );
    if (smartFillSource === "nominatim")
      return (
        <Chip
          size="small"
          icon={<CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />}
          label="Location found — details filled in automatically"
          color="info"
          variant="outlined"
          sx={{ mt: 0.75, height: 24, fontSize: 11 }}
        />
      );
    if (smartFillSource === "manual")
      return (
        <Chip
          size="small"
          icon={<EditNoteOutlinedIcon sx={{ fontSize: "14px !important" }} />}
          label="We couldn't find this venue — please fill in the address below"
          color="warning"
          variant="outlined"
          sx={{ mt: 0.75, height: 24, fontSize: 11 }}
        />
      );
    return null;
  };

  const isFuneral = bookingType === "FUNERAL";
  const isConference = bookingType === "CONFERENCE";

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      "& fieldset": { borderColor: colors.primary[300] },
      "&:hover fieldset": { borderColor: colors.primary[400] },
      "&.Mui-focused fieldset": { borderColor: colors.primary[400] },
    },
    "& .MuiInputLabel-root": { color: colors.primary[300] },
    "& .MuiInputBase-input": { color: colors.primary[100] },
  };

  // Shared expanded details props
  const expandedDetailsProps = {
    smartSearchTerm,
    smartSuggestions,
    smartLoading,
    smartShowDropdown,
    smartFillSource,
    smartNeedsManual,
    smartVenueData,
    statusChip: smartStatusChip(),
    packagesLoading,
    packages,
    selectedPackageId: quickInfo.selected_package_id,
    onSearchChange: handleSmartSearchChange,
    onSelect: handleSmartSelect,
    onShowDropdown: setSmartShowDropdown,
    onPackageChange: handlePackageChange,
    onManualFieldChange: handleManualFieldChange,
    bookingType,
    serviceDate: when,
    startTime: whatTime,
    quickInfo,
    fieldSx,
    onQuickInfoChange: handleQuickInfoChange,
    onPhoneChange: (value: string) => {
      setQuickInfo((p) => ({ ...p, phone: value }));
    },
  };

  // Shared location quick pick props
  const locationQuickPickProps = {
    funeralLocation,
    locationAnchor,
    locationBoxRef,
    borderColor: colors.primary[300],
    hoverBorderColor: colors.primary[400],
    onOpen: () => setLocationAnchor(locationBoxRef.current),
    onClose: () => setLocationAnchor(null),
    onChange: (loc: string) => setFuneralLocation(loc),
    onDelete: () => setFuneralLocation(""),
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      <BookingInputCard
        when={when}
        bookingType={bookingType}
        whatTime={whatTime}
        typeBoxRef={typeBoxRef}
        timeBoxRef={timeBoxRef}
        onWhenClick={(el) => setWhenAnchor(el)}
        onTypeClick={(el) => setTypeAnchor(el)}
        onTimeClick={(el) => setTimeAnchor(el)}
        onCreateBooking={handleCreateBooking}
        formatDate={formatDate}
        formatTime={formatTime}
        formatType={formatType}
        buttonColor={buttonColor}
      />

      <DatePickerDropdown
        open={Boolean(whenAnchor)}
        anchorEl={whenAnchor}
        onClose={() => setWhenAnchor(null)}
        value={when}
        onChange={(date) => {
          setWhen(date);
          setWhenAnchor(null);
          if (typeBoxRef.current) setTypeAnchor(typeBoxRef.current);
        }}
        disablePast={true}
        showFlexibleDates={true}
      />

      <Popper
        open={Boolean(typeAnchor)}
        anchorEl={typeAnchor}
        placement="bottom-start"
        sx={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setTypeAnchor(null)}>
          <Paper elevation={8} sx={{ py: 1, borderRadius: 3, minWidth: 180 }}>
            {BOOKING_TYPES.map((type) => (
              <Box
                key={type.value}
                onClick={() => {
                  setBookingType(type.value);
                  setTypeAnchor(null);
                  if (timeBoxRef.current) setTimeAnchor(timeBoxRef.current);
                }}
                sx={{
                  px: 2.5,
                  py: 1.2,
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  fontWeight: bookingType === type.value ? 600 : 400,
                  backgroundColor:
                    bookingType === type.value
                      ? "action.selected"
                      : "transparent",
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                {type.label}
              </Box>
            ))}
          </Paper>
        </ClickAwayListener>
      </Popper>

      <TimePickerDropdown
        open={Boolean(timeAnchor)}
        anchorEl={timeAnchor}
        onClose={() => setTimeAnchor(null)}
        value={whatTime}
        onChange={(time) => {
          setWhatTime(time);
          setTimeAnchor(null);
        }}
        format="12h"
      />

      {/* ── Main dialog ──────────────────────────────────────────────────── */}
      <Dialog
        open={quickInfoOpen}
        onClose={() => setQuickInfoOpen(false)}
        maxWidth="xs"
        fullWidth
        disableRestoreFocus
        PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}
      >
        <QuickBookingHeader bookingType={formatType(bookingType)} />

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            pt: "20px !important",
            overflowY: "auto",
          }}
        >
          {/* ── Client ─────────────────────────────────────────────────── */}
          <Box>
            <SectionLabel>Client Details</SectionLabel>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <TextField
                label="First Name"
                size="small"
                fullWidth
                sx={fieldSx}
                value={quickInfo.first_name}
                onChange={(e) =>
                  setQuickInfo((p) => ({ ...p, first_name: e.target.value }))
                }
              />
              <TextField
                label="Surname"
                size="small"
                fullWidth
                sx={fieldSx}
                value={quickInfo.surname}
                onChange={(e) =>
                  setQuickInfo((p) => ({ ...p, surname: e.target.value }))
                }
              />
              <MuiTelInput
                size="small"
                label="Phone"
                value={quickInfo.phone}
                onChange={(value: string, _info: MuiTelInputInfo) => {
                  void _info;
                  setQuickInfo((p) => ({ ...p, phone: value }));
                }}
                defaultCountry="ZW"
                variant="outlined"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    "& fieldset": { borderColor: colors.primary[300] },
                    "&:hover fieldset": { borderColor: colors.primary[400] },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.primary[400],
                    },
                  },
                  "& .MuiInputLabel-root": { color: colors.primary[300] },
                  "& .MuiInputBase-input": { color: colors.primary[100] },
                }}
              />
            </Box>
          </Box>

          {/* ── Funeral ────────────────────────────────────────────────── */}
          {isFuneral && (
            <Box>
              <SectionLabel>Deceased Details</SectionLabel>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <TextField
                  label="First Name"
                  size="small"
                  fullWidth
                  sx={fieldSx}
                  value={quickInfo.deceased_name}
                  onChange={(e) =>
                    setQuickInfo((p) => ({
                      ...p,
                      deceased_name: e.target.value,
                    }))
                  }
                />
                <TextField
                  label="Surname"
                  size="small"
                  fullWidth
                  sx={fieldSx}
                  value={quickInfo.deceased_surname}
                  onChange={(e) =>
                    setQuickInfo((p) => ({
                      ...p,
                      deceased_surname: e.target.value,
                    }))
                  }
                />
                <FormControl>
                  <FormLabel sx={{ fontSize: "0.75rem", mb: 0.5 }}>
                    Gender
                  </FormLabel>
                  <RadioGroup
                    row
                    value={quickInfo.gender}
                    onChange={(e) =>
                      setQuickInfo((p) => ({ ...p, gender: e.target.value }))
                    }
                  >
                    <FormControlLabel
                      value="Male"
                      control={<Radio size="small" />}
                      label="Male"
                    />
                    <FormControlLabel
                      value="Female"
                      control={<Radio size="small" />}
                      label="Female"
                    />
                  </RadioGroup>
                </FormControl>
                <FormControl fullWidth size="small">
                  <CustomSelect
                    label="Type of Service"
                    value={quickInfo.type_of_service}
                    onChange={(e) =>
                      setQuickInfo((p) => ({
                        ...p,
                        type_of_service: e.target.value as string,
                      }))
                    }
                  >
                    <MenuItem value="Funeral">Funeral</MenuItem>
                    <MenuItem value="Celebration">Celebration of Life</MenuItem>
                    <MenuItem value="Burial">Burial</MenuItem>
                  </CustomSelect>
                </FormControl>

                <LocationQuickPick
                  {...locationQuickPickProps}
                  title="Where is the service?"
                />
              </Box>
            </Box>
          )}

          {isFuneral && showFullDetails && (
            <ExpandedDetails {...expandedDetailsProps} />
          )}

          {/* ── Conference ─────────────────────────────────────────────── */}
          {isConference && (
            <Box>
              <SectionLabel>Conference Details</SectionLabel>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <TextField
                  label="Conference Name"
                  size="small"
                  fullWidth
                  sx={fieldSx}
                  value={quickInfo.conference_name}
                  onChange={(e) =>
                    setQuickInfo((p) => ({
                      ...p,
                      conference_name: e.target.value,
                    }))
                  }
                />
                <FormControl fullWidth size="small">
                  <CustomSelect
                    label="Conference Type"
                    value={quickInfo.conference_type}
                    onChange={(e) =>
                      setQuickInfo((p) => ({
                        ...p,
                        conference_type: e.target.value as string,
                      }))
                    }
                  >
                    {[
                      { value: "summit", label: "Summit" },
                      { value: "workshop", label: "Workshop" },
                      { value: "symposium", label: "Symposium" },
                      { value: "dinner", label: "Dinner" },
                      {
                        value: "breakfast_meeting",
                        label: "Breakfast Meeting",
                      },
                      { value: "congress", label: "Congress" },
                      { value: "conference", label: "Conference" },
                      { value: "other", label: "Other" },
                    ].map(({ value, label }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </FormControl>
              </Box>
            </Box>
          )}

          {isConference && showFullDetails && (
            <ExpandedDetails {...expandedDetailsProps} />
          )}
        </DialogContent>

        {/* ── Actions ──────────────────────────────────────────────────── */}
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          {!showFullDetails && (
            <Button
              variant="outlined"
              onClick={() => setShowFullDetails(true)}
              sx={{ flex: 1, borderRadius: 3, textTransform: "none" }}
            >
              Add More Details
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSubmitting}
            fullWidth={showFullDetails}
            sx={{
              flex: showFullDetails ? undefined : 1,
              borderRadius: 3,
              textTransform: "none",
              backgroundColor: buttonColor,
              "&:hover": { backgroundColor: buttonColor, opacity: 0.9 },
            }}
          >
            {isSubmitting ? "Saving…" : "Save Booking"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add-venue dialog ─────────────────────────────────────────────── */}
      <Dialog
        open={openVenueDialog}
        onClose={() => setOpenVenueDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3, fontWeight: 700, fontSize: "1.1rem" }}>
          Add New Venue
        </Box>
        <DialogContent>
          {(
            [
              { name: "name", label: "Venue Name", required: true },
              { name: "address_one", label: "Address One", required: true },
              { name: "address_two", label: "Address Two", required: false },
              { name: "suburb", label: "Suburb", required: false },
            ] as const
          ).map(({ name, label, required }) => (
            <CustomTextField
              key={name}
              fullWidth
              label={label}
              name={name}
              value={newVenue[name] ?? ""}
              onChange={(e) =>
                setNewVenue((prev) => ({ ...prev, [name]: e.target.value }))
              }
              variant="outlined"
              margin="normal"
              required={required}
            />
          ))}
          <CustomTextField
            fullWidth
            label="City"
            name="city"
            value={newVenue.city ?? ""}
            onChange={(e) =>
              setNewVenue((prev) => ({ ...prev, city: e.target.value }))
            }
            variant="outlined"
            margin="normal"
            select
            required
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {reduxTheme.cities?.map((city: string) => (
              <MenuItem key={city} value={city}>
                {city}
              </MenuItem>
            ))}
          </CustomTextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVenueDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleAddVenue}
            variant="contained"
            disabled={
              addingVenue ||
              !newVenue.name ||
              !newVenue.address_one ||
              !newVenue.city
            }
          >
            {addingVenue ? <CircularProgress size={20} /> : "Add Venue"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReferralQuickBookingForm;
